export interface ExcelWeekItem {
  weekNum: number;
  month: string;
  label: string;
  startDate: string;
  endDate: string;
  displayStart: string;
  displayEnd: string;
  formattedLabel: string;
}

const MONTH_ABBR = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sept', 'oct', 'nov', 'dec'];

export function formatWeekDateStr(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const y = parts[0];
  const m = parseInt(parts[1], 10);
  const d = parts[2];
  const mStr = MONTH_ABBR[m - 1] || 'jan';
  return `${d}-${mStr}-${y}`;
}

export function formatWeekDateStrShort(dateStr: string): string {
  if (!dateStr) return '';
  const parts = dateStr.split('-');
  if (parts.length !== 3) return dateStr;
  const y = parts[0].slice(-2);
  const m = parseInt(parts[1], 10);
  const d = parts[2];
  const mStr = MONTH_ABBR[m - 1] || 'jan';
  return `${d}-${mStr}-${y}`;
}

export function formatWeekDisplayLabel(startDate: string, endDate: string, label?: string, monthName?: string): string {
  const sFormatted = formatWeekDateStr(startDate);
  const eFormatted = formatWeekDateStr(endDate);
  let prefix = label || 'Week 1';
  if (prefix.toLowerCase().startsWith('week')) {
    const num = prefix.replace(/\D/g, '');
    prefix = `Week ${num}`;
  }

  const matched = RAW_SCHEDULE.find(w => w.startDate === startDate && w.endDate === endDate);
  const cap = (s: string) => s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '';
  let mSuffix = '';
  if (monthName) {
    mSuffix = ` (${cap(monthName)})`;
  } else if (matched) {
    mSuffix = ` (${cap(matched.month)})`;
  } else if (endDate) {
    const parts = endDate.split('-');
    if (parts.length === 3) {
      const m = parseInt(parts[1], 10);
      const mStr = MONTH_ABBR[m - 1] || 'sept';
      mSuffix = ` (${cap(mStr)})`;
    }
  }

  return `${prefix}: ${sFormatted} to ${eFormatted}${mSuffix}`;
}

export function formatMonthDisplayLabel(year: number, month: number): string {
  const MONTH_MAP: Record<string, number> = {
    'Jan': 1, 'Feb': 2, 'Mar': 3, 'Apr': 4, 'May': 5, 'Jun': 6,
    'Jul': 7, 'Aug': 8, 'Sept': 9, 'Sep': 9, 'Oct': 10, 'Nov': 11, 'Dec': 12,
  };

  const MONTH_SHORT = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sept', 'Oct', 'Nov', 'Dec'];
  const mName = `${MONTH_SHORT[month - 1]} ${year}`;

  const excelMatches = RAW_SCHEDULE.filter(w => {
    const mNum = MONTH_MAP[w.month];
    if (mNum !== month) return false;
    const sYear = parseInt(w.startDate.split('-')[0], 10);
    const eYear = parseInt(w.endDate.split('-')[0], 10);
    return sYear === year || eYear === year;
  });

  if (excelMatches.length > 0) {
    const sDate = excelMatches[0].startDate;
    const eDate = excelMatches[excelMatches.length - 1].endDate;
    return `${mName} (${formatWeekDateStrShort(sDate)} to ${formatWeekDateStrShort(eDate)})`;
  }

  const mStr = String(month).padStart(2, '0');
  const lastDay = new Date(year, month, 0).getDate();
  const sDate = `${year}-${mStr}-01`;
  const eDate = `${year}-${mStr}-${String(lastDay).padStart(2, '0')}`;
  return `${mName} (${formatWeekDateStrShort(sDate)} to ${formatWeekDateStrShort(eDate)})`;
}

const RAW_SCHEDULE = [
  { weekNum: 1,  month: 'Sept', label: 'week1',  startDate: '2026-08-31', endDate: '2026-09-06', displayStart: '31/08/26', displayEnd: '06/09/26' },
  { weekNum: 2,  month: 'Sept', label: 'week2',  startDate: '2026-09-07', endDate: '2026-09-13', displayStart: '07/09/26', displayEnd: '13/09/26' },
  { weekNum: 3,  month: 'Sept', label: 'week3',  startDate: '2026-09-14', endDate: '2026-09-20', displayStart: '14/09/26', displayEnd: '20/09/26' },
  { weekNum: 4,  month: 'Sept', label: 'week4',  startDate: '2026-09-21', endDate: '2026-09-27', displayStart: '21/09/26', displayEnd: '27/09/26' },
  { weekNum: 5,  month: 'Oct',  label: 'week5',  startDate: '2026-09-28', endDate: '2026-10-04', displayStart: '28/09/26', displayEnd: '04/10/26' },
  { weekNum: 6,  month: 'Oct',  label: 'week6',  startDate: '2026-10-05', endDate: '2026-10-11', displayStart: '05/10/26', displayEnd: '11/10/26' },
  { weekNum: 7,  month: 'Oct',  label: 'week7',  startDate: '2026-10-12', endDate: '2026-10-18', displayStart: '12/10/26', displayEnd: '18/10/26' },
  { weekNum: 8,  month: 'Oct',  label: 'week8',  startDate: '2026-10-19', endDate: '2026-10-25', displayStart: '19/10/26', displayEnd: '25/10/26' },
  { weekNum: 9,  month: 'Oct',  label: 'week9',  startDate: '2026-10-26', endDate: '2026-11-01', displayStart: '26/10/26', displayEnd: '01/11/26' },
  { weekNum: 10, month: 'Nov',  label: 'week10', startDate: '2026-11-02', endDate: '2026-11-08', displayStart: '02/11/26', displayEnd: '08/11/26' },
  { weekNum: 11, month: 'Nov',  label: 'week11', startDate: '2026-11-09', endDate: '2026-11-15', displayStart: '09/11/26', displayEnd: '15/11/26' },
  { weekNum: 12, month: 'Nov',  label: 'week12', startDate: '2026-11-16', endDate: '2026-11-22', displayStart: '16/11/26', displayEnd: '22/11/26' },
  { weekNum: 13, month: 'Nov',  label: 'week13', startDate: '2026-11-23', endDate: '2026-11-29', displayStart: '23/11/26', displayEnd: '29/11/26' },
  { weekNum: 14, month: 'Dec',  label: 'week14', startDate: '2026-11-30', endDate: '2026-12-06', displayStart: '30/11/26', displayEnd: '06/12/26' },
  { weekNum: 15, month: 'Dec',  label: 'week15', startDate: '2026-12-07', endDate: '2026-12-13', displayStart: '07/12/26', displayEnd: '13/12/26' },
  { weekNum: 16, month: 'Dec',  label: 'week16', startDate: '2026-12-14', endDate: '2026-12-20', displayStart: '14/12/26', displayEnd: '20/12/26' },
  { weekNum: 17, month: 'Dec',  label: 'week17', startDate: '2026-12-21', endDate: '2026-12-27', displayStart: '21/12/26', displayEnd: '27/12/26' },
  { weekNum: 18, month: 'Jan',  label: 'week18', startDate: '2026-12-28', endDate: '2027-01-03', displayStart: '28/12/26', displayEnd: '03/01/27' },
  { weekNum: 19, month: 'Jan',  label: 'week19', startDate: '2027-01-04', endDate: '2027-01-10', displayStart: '04/01/27', displayEnd: '10/01/27' },
  { weekNum: 20, month: 'Jan',  label: 'week20', startDate: '2027-01-11', endDate: '2027-01-17', displayStart: '11/01/27', displayEnd: '17/01/27' },
  { weekNum: 21, month: 'Jan',  label: 'week21', startDate: '2027-01-18', endDate: '2027-01-24', displayStart: '18/01/27', displayEnd: '24/01/27' },
  { weekNum: 22, month: 'Jan',  label: 'week22', startDate: '2027-01-25', endDate: '2027-01-31', displayStart: '25/01/27', displayEnd: '31/01/27' },
  { weekNum: 23, month: 'Feb',  label: 'week23', startDate: '2027-02-01', endDate: '2027-02-07', displayStart: '01/02/27', displayEnd: '07/02/27' },
  { weekNum: 24, month: 'Feb',  label: 'week24', startDate: '2027-02-08', endDate: '2027-02-14', displayStart: '08/02/27', displayEnd: '14/02/27' },
  { weekNum: 25, month: 'Feb',  label: 'week25', startDate: '2027-02-15', endDate: '2027-02-21', displayStart: '15/02/27', displayEnd: '21/02/27' },
  { weekNum: 26, month: 'Feb',  label: 'week26', startDate: '2027-02-22', endDate: '2027-02-28', displayStart: '22/02/27', displayEnd: '28/02/27' },
  { weekNum: 27, month: 'Mar',  label: 'week27', startDate: '2027-03-01', endDate: '2027-03-07', displayStart: '01/03/27', displayEnd: '07/03/27' },
  { weekNum: 28, month: 'Mar',  label: 'week28', startDate: '2027-03-08', endDate: '2027-03-14', displayStart: '08/03/27', displayEnd: '14/03/27' },
  { weekNum: 29, month: 'Mar',  label: 'week29', startDate: '2027-03-15', endDate: '2027-03-21', displayStart: '15/03/27', displayEnd: '21/03/27' },
  { weekNum: 30, month: 'Mar',  label: 'week30', startDate: '2027-03-22', endDate: '2027-03-28', displayStart: '22/03/27', displayEnd: '28/03/27' },
  { weekNum: 31, month: 'Apr',  label: 'week31', startDate: '2027-03-29', endDate: '2027-04-04', displayStart: '29/03/27', displayEnd: '04/04/27' },
  { weekNum: 32, month: 'Apr',  label: 'week32', startDate: '2027-04-05', endDate: '2027-04-11', displayStart: '05/04/27', displayEnd: '11/04/27' },
  { weekNum: 33, month: 'Apr',  label: 'week33', startDate: '2027-04-12', endDate: '2027-04-18', displayStart: '12/04/27', displayEnd: '18/04/27' },
  { weekNum: 34, month: 'Apr',  label: 'week34', startDate: '2027-04-19', endDate: '2027-04-25', displayStart: '19/04/27', displayEnd: '25/04/27' },
  { weekNum: 35, month: 'Apr',  label: 'week35', startDate: '2027-04-26', endDate: '2027-05-02', displayStart: '26/04/27', displayEnd: '02/05/27' },
  { weekNum: 36, month: 'May',  label: 'week36', startDate: '2027-05-03', endDate: '2027-05-09', displayStart: '03/05/27', displayEnd: '09/05/27' },
];

export const EXCEL_WEEK_SCHEDULE: ExcelWeekItem[] = RAW_SCHEDULE.map(item => ({
  ...item,
  formattedLabel: formatWeekDisplayLabel(item.startDate, item.endDate, item.label),
}));

/** Excel / DOM680 operational months (aligned with reports.service schedule) */
export const EXCEL_OPERATIONAL_MONTHS: Array<{ year: number; month: number; start: string; end: string }> = [
  { year: 2026, month: 9, start: '2026-08-31', end: '2026-09-27' },
  { year: 2026, month: 10, start: '2026-09-28', end: '2026-11-01' },
  { year: 2026, month: 11, start: '2026-11-02', end: '2026-11-29' },
  { year: 2026, month: 12, start: '2026-11-30', end: '2026-12-27' },
  { year: 2027, month: 1, start: '2026-12-28', end: '2027-01-31' },
  { year: 2027, month: 2, start: '2027-02-01', end: '2027-02-28' },
  { year: 2027, month: 3, start: '2027-03-01', end: '2027-03-28' },
  { year: 2027, month: 4, start: '2027-03-29', end: '2027-05-02' },
];

export function getExcelOperationalMonthFilterOptions(): Array<{ label: string; value: string }> {
  return EXCEL_OPERATIONAL_MONTHS.map((m) => ({
    label: formatMonthDisplayLabel(m.year, m.month),
    value: `${m.start}|${m.end}`,
  }));
}

/**
 * Default Master Yearly Cycle for DOM680 Target Planner.
 * Derived from Excel week schedule span (first week month → last week month).
 * Current sheet: Sept 2026 — May 2027. Week/month filters stay on their own schedules.
 */
export function getDefaultExcelYearlyCycle(): {
  startMonth: number;
  startYear: number;
  endMonth: number;
  endYear: number;
} {
  const MONTH_MAP: Record<string, number> = {
    Jan: 1, Feb: 2, Mar: 3, Apr: 4, May: 5, Jun: 6,
    Jul: 7, Aug: 8, Sept: 9, Sep: 9, Oct: 10, Nov: 11, Dec: 12,
  };
  const first = EXCEL_WEEK_SCHEDULE[0];
  const last = EXCEL_WEEK_SCHEDULE[EXCEL_WEEK_SCHEDULE.length - 1];
  const startMonth = MONTH_MAP[first.month] || 9;
  const endMonth = MONTH_MAP[last.month] || 5;
  // Prefer label-month year: Sept weeks are 2026; May weeks end in 2027
  const startYear = Number(first.endDate.slice(0, 4));
  const endYear = Number(last.endDate.slice(0, 4));
  return { startMonth, startYear, endMonth, endYear };
}
